import React, { useState, useEffect, useCallback } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { DesignSystem, getColor, getSpacing, getShadow } from '../styles/designSystem';
import { API_BASE_URL } from '../api/tourism';
import type { StateGrouped } from '../hooks/useHeaderItineraries';

interface HeaderProps {
  onNavigateTo?: (page: string, data?: any) => void;
  currentPage?: string;
  transparent?: boolean;
  forceScrolled?: boolean;
  destinationItineraries?: StateGrouped;
  trekkingItineraries?: StateGrouped;
  bikeItineraries?: StateGrouped;
}

// Transparent mode styles — scroll-aware, frosted glass on scroll
const transparentStyles = css<{ $scrolled?: boolean }>`
  background: ${props => props.$scrolled ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  backdrop-filter: ${props => props.$scrolled ? 'blur(2px)' : 'none'};
  -webkit-backdrop-filter: ${props => props.$scrolled ? 'blur(2px)' : 'none'};
  border-bottom: 1px solid ${props => props.$scrolled ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  box-shadow: none;
`;

// Opaque (default) mode styles
const opaqueStyles = css`
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${getColor('primary.100')};
  box-shadow: ${getShadow('subtle')};
`;

const HeaderSection = styled.header<{ $transparent?: boolean; $scrolled?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: ${getSpacing('sm')} 0;
  height: 56px;
  display: flex;
  align-items: center;
  transition: background 0.15s ease, border-bottom 0.15s ease;
  will-change: background;
  
  ${props => props.$transparent ? transparentStyles : opaqueStyles}
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${getSpacing('lg')};
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: ${getSpacing('xl')};
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSpacing('sm')};
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  
  &:hover {
    opacity: 0.9;
    transform: scale(1.02);
  }
`;

const LogoImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
`;

const LogoText = styled.h1<{ $white?: boolean }>`
  color: ${props => props.$white ? 'white' : '#8B7355'};
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  font-family: ${DesignSystem.typography.fonts.display};
  white-space: nowrap;
  transition: color 0.15s ease;
  text-shadow: ${props => props.$white ? '0 1px 8px rgba(0,0,0,0.3)' : 'none'};
  
  span {
    color: ${props => props.$white ? 'white' : '#8B7355'};
  }
  
  @media (max-width: 768px) {
    font-size: 1.0rem;
  }
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    font-size: 0.9rem;
  }
`;

const NavLinks = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: ${getSpacing('xl')};
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.li<{ active?: boolean; $white?: boolean }>`
  display: flex;
  align-items: center;
  
  a {
    color: ${props => {
      if (props.$white) return props.active ? '#CAB96E' : 'white';
      return props.active ? '#8B7355' : getColor('primary.700');
    }};
    text-decoration: none;
    font-weight: 500;
    font-size: 1rem;
    letter-spacing: -0.01em;
    transition: all ${DesignSystem.motion.duration.fast} ease;
    position: relative;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding-bottom: 6px;
    text-shadow: ${props => props.$white ? '0 1px 6px rgba(0,0,0,0.3)' : 'none'};
    
    &::after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 0;
      width: ${props => props.active ? '100%' : '0'};
      height: 2px;
      background: ${props => props.$white ? '#CAB96E' : '#8B7355'};
      transition: width ${DesignSystem.motion.duration.normal} ease;
    }
    
    &:hover {
      color: ${props => props.$white ? '#CAB96E' : '#8B7355'};
      
      &::after {
        width: 100%;
      }
    }
  }
`;

// Dropdown Menu Styled Components
const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownTrigger = styled.button<{ $white?: boolean }>`
  background: none;
  border: none;
  color: ${props => props.$white ? 'white' : getColor('primary.700')};
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: -0.01em;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  cursor: pointer;
  padding: 0;
  padding-bottom: 6px;
  position: relative;
  text-shadow: ${props => props.$white ? '0 1px 6px rgba(0,0,0,0.3)' : 'none'};
  display: flex;
  align-items: center;
  gap: 6px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${props => props.$white ? '#CAB96E' : '#8B7355'};
    transition: width ${DesignSystem.motion.duration.normal} ease;
  }
  
  &:hover {
    color: ${props => props.$white ? '#CAB96E' : '#8B7355'};
    
    &::after {
      width: 100%;
    }
  }
`;

const DropdownChevron = styled.span<{ $isOpen?: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transition: transform ${DesignSystem.motion.duration.fast} ease;
  transform: ${props => props.$isOpen ? 'rotate(-135deg)' : 'rotate(45deg)'};
  margin-bottom: ${props => props.$isOpen ? '-2px' : '2px'};
`;

const DropdownMenu = styled.div<{ $isOpen?: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  box-shadow: ${getShadow('large')};
  min-width: 280px;
  margin-top: 12px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all ${DesignSystem.motion.duration.normal} ease;
  z-index: 1000;
  padding: 8px 0 0 0;
  border: 1px solid ${getColor('primary.100')};
  overflow: hidden;
`;

const DropdownStateHeader = styled.div`
  padding: 10px 20px 6px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${getColor('primary.500')};
`;

const DropdownItem = styled.a`
  display: block;
  padding: 10px 20px;
  color: ${getColor('primary.900')};
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 400;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  cursor: pointer;
  border-left: 3px solid transparent;
  
  &:hover {
    background: ${getColor('primary.50')};
    border-left-color: ${getColor('accent.600')};
    padding-left: 24px;
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: ${getColor('primary.100')};
  margin: 4px 0;
`;

const BikePreviewItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 20px;
  border-bottom: 1px solid ${getColor('primary.50')};
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  
  &:hover {
    background: ${getColor('primary.50')};
    padding-left: 24px;
  }
`;

const BikeItemTitle = styled.div`
  font-weight: 500;
  color: ${getColor('primary.900')};
  font-size: 0.85rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BikeItemMeta = styled.div`
  font-size: 0.7rem;
  color: ${getColor('primary.500')};
  display: flex;
  gap: 8px;
  letter-spacing: 0.02em;
`;

const ExploreAllLink = styled.div`
  padding: 12px 20px;
  text-align: center;
  background: rgba(0, 0, 0, 0.85);
  color: #CAB96E;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  letter-spacing: 0.04em;
  font-family: ${DesignSystem.typography.fonts.display};
  
  &:hover {
    background: rgba(0, 0, 0, 0.95);
    color: #D4C478;
  }
`;

// ─── Mobile Menu ──────────────────────────────────────────────────────────────

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${getSpacing('sm')};
  z-index: 101;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;
    min-width: 44px;
    min-height: 44px;
  }
`;

const HamburgerLine = styled.span<{ $white?: boolean }>`
  display: block;
  width: 22px;
  height: 2px;
  background: ${props => props.$white ? 'white' : '#8B7355'};
  border-radius: 1px;
  transition: all 0.15s ease;
`;

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const MobileMenuOverlay = styled.div<{ $isOpen: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: ${props => props.$isOpen ? 1 : 0};
    visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
    pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
    transition: all 0.3s ease;
  }
`;

const MobileMenuDrawer = styled.div<{ $isOpen: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 300px;
    max-width: 85vw;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    z-index: 1000;
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform 0.3s ease;
    overflow-y: auto;
    pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
    box-shadow: ${props => props.$isOpen ? getShadow('large') : 'none'};
  }
`;

const MobileDrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${getSpacing('lg')} ${getSpacing('xl')};
  border-bottom: 1px solid ${getColor('primary.100')};
`;

const MobileDrawerTitle = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #8B7355;
  font-family: ${DesignSystem.typography.fonts.display};
  letter-spacing: -0.01em;
`;

const MobileCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${getColor('primary.600')};
  cursor: pointer;
  padding: ${getSpacing('xs')};
  line-height: 1;
  transition: color 0.2s ease;
  &:hover { color: ${getColor('primary.900')}; }
`;

const MobileNavList = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${getSpacing('md')} 0;
`;

const MobileNavItem = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? getColor('primary.50') : 'none'};
  border: none;
  text-align: left;
  padding: ${getSpacing('md')} ${getSpacing('xl')};
  font-size: 1rem;
  font-weight: 500;
  color: ${props => props.$active ? '#8B7355' : getColor('primary.800')};
  cursor: pointer;
  font-family: ${DesignSystem.typography.fonts.primary};
  transition: all 0.2s ease;
  border-left: 3px solid ${props => props.$active ? '#8B7355' : 'transparent'};
  &:hover {
    background: ${getColor('primary.50')};
    color: #8B7355;
  }
`;

const MobileDropdownToggle = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  text-align: left;
  padding: ${getSpacing('md')} ${getSpacing('xl')};
  font-size: 1rem;
  font-weight: 500;
  color: ${getColor('primary.800')};
  cursor: pointer;
  font-family: ${DesignSystem.typography.fonts.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  transition: all 0.2s ease;
  &:hover {
    background: ${getColor('primary.50')};
    color: #8B7355;
  }
`;

const MobileDropdownChevron = styled.span<{ $isOpen?: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(-135deg)' : 'rotate(45deg)'};
  color: ${getColor('primary.500')};
  margin-bottom: ${props => props.$isOpen ? '-2px' : '2px'};
`;

const MobileDropdownItems = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: ${getColor('primary.50')};
`;

const MobileDropdownItem = styled.button`
  background: none;
  border: none;
  text-align: left;
  padding: ${getSpacing('sm')} ${getSpacing('xl')} ${getSpacing('sm')} ${getSpacing('3xl')};
  font-size: 0.9rem;
  font-weight: 400;
  color: ${getColor('primary.700')};
  cursor: pointer;
  width: 100%;
  font-family: ${DesignSystem.typography.fonts.primary};
  transition: all 0.2s ease;
  line-height: 1.4;
  &:hover {
    background: ${getColor('primary.100')};
    color: #8B7355;
  }
`;

const MobileDropdownLabel = styled.div`
  padding: ${getSpacing('sm')} ${getSpacing('xl')} ${getSpacing('xs')} ${getSpacing('2xl')};
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${getColor('primary.500')};
`;


const STATE_LABELS: Record<string, string> = { ladakh: 'Ladakh', kashmir: 'Kashmir', himachal: 'Himachal Pradesh' };
const STATE_KEYS = ['ladakh', 'kashmir', 'himachal'] as const;

const emptyGrouped: StateGrouped = { ladakh: [], kashmir: [], himachal: [] };

export const AppHeader: React.FC<HeaderProps> = ({ 
  onNavigateTo, 
  currentPage = 'home', 
  transparent = false,
  forceScrolled = false,
  destinationItineraries = emptyGrouped,
  trekkingItineraries = emptyGrouped,
  bikeItineraries = emptyGrouped
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);
  const dropdownRef = React.useRef<HTMLUListElement>(null);
  
  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 80);
  }, []);
  
  useEffect(() => {
    if (transparent) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [transparent, handleScroll]);

  // Handle click-outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdown]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setMobileDropdown(null);
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleNavClick = (page: string) => {
    setOpenDropdown(null);
    closeMobileMenu();
    switch (page) {
      case 'home': {
        const homeSection = document.getElementById('home');
        if (homeSection) {
          homeSection.scrollIntoView({ behavior: 'smooth' });
        } else {
          if (onNavigateTo) {
            onNavigateTo('home');
          }
        }
        break;
      }
      case 'destinations': {
        if (onNavigateTo) {
          onNavigateTo('destinations');
        } else {
          const destinationsSection = document.getElementById('destinations');
          if (destinationsSection) {
            destinationsSection.scrollIntoView({ behavior: 'smooth' });
          }
        }
        break;
      }
      case 'contact':
        if (onNavigateTo) {
          onNavigateTo('contact');
        }
        break;
      default:
        if (onNavigateTo) {
          onNavigateTo(page);
        }
    }
  };

  const handleDestinationSelect = (itinerary: any) => {
    setOpenDropdown(null);
    closeMobileMenu();
    if (onNavigateTo) {
      onNavigateTo('detailed-itinerary', {
        state: itinerary.state?.[0] || 'Ladakh',
        itineraryId: itinerary.id,
        fromHomepage: true
      });
    }
  };

  const handleTrekSelect = (trek: any) => {
    setOpenDropdown(null);
    closeMobileMenu();
    if (onNavigateTo) {
      onNavigateTo('trek-detail', {
        state: trek.state?.[0] || trek.region?.split(',')[0] || 'Ladakh',
        itineraryId: trek.id,
        fromHomepage: true
      });
    }
  };

  const handleBikeSelect = (bike: any) => {
    setOpenDropdown(null);
    closeMobileMenu();
    if (onNavigateTo) {
      onNavigateTo('detailed-itinerary', {
        itineraryId: bike.id,
        bikeItinerary: true
      });
    }
  };

  const useWhiteText = transparent && !scrolled && !forceScrolled;

  return (
    <>
    <HeaderSection $transparent={transparent} $scrolled={scrolled}>
      <Container>
        <Nav>
          <LogoContainer onClick={() => handleNavClick('home')}>
            <LogoImage src={`${API_BASE_URL}/api/images/app_assets/logo.webp`} alt="Himalayan Rovers" />
            <LogoText $white={useWhiteText}><span>HIMALAYAN</span> ROVERS</LogoText>
          </LogoContainer>
          <HamburgerButton onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
            <HamburgerLine $white={useWhiteText} />
            <HamburgerLine $white={useWhiteText} />
            <HamburgerLine $white={useWhiteText} />
          </HamburgerButton>
          <NavLinks ref={dropdownRef}>
            <NavLink active={currentPage === 'home'} $white={useWhiteText}>
              <a onClick={() => handleNavClick('home')}>Home</a>
            </NavLink>

            {/* Destinations Dropdown */}
            <NavLink $white={useWhiteText} style={{ position: 'relative' }}>
              <DropdownContainer>
                <DropdownTrigger 
                  $white={useWhiteText}
                  onClick={() => setOpenDropdown(openDropdown === 'destinations' ? null : 'destinations')}
                >
                  Destinations
                  <DropdownChevron $isOpen={openDropdown === 'destinations'} />
                </DropdownTrigger>
                
                <DropdownMenu $isOpen={openDropdown === 'destinations'}>
                  {STATE_KEYS.map((stateKey) => {
                    const items = destinationItineraries[stateKey];
                    if (!items || items.length === 0) return null;
                    return (
                      <React.Fragment key={`dest-${stateKey}`}>
                        <DropdownStateHeader>{STATE_LABELS[stateKey]}</DropdownStateHeader>
                        {items.slice(0, 3).map((itinerary: any, idx: number) => (
                          <BikePreviewItem
                            key={`${stateKey}-${idx}`}
                            onClick={() => handleDestinationSelect(itinerary)}
                          >
                            <BikeItemTitle>{itinerary.title || itinerary.name}</BikeItemTitle>
                            <BikeItemMeta>
                              <span>{itinerary.packageDuration}</span>
                            </BikeItemMeta>
                          </BikePreviewItem>
                        ))}
                        <DropdownDivider />
                      </React.Fragment>
                    );
                  })}
                  <ExploreAllLink onClick={() => {
                    setOpenDropdown(null);
                    if (onNavigateTo) onNavigateTo('curated-experiences', { destination: null, fromHomepage: true, tourType: 'car' });
                  }}>
                    Explore All Destinations →
                  </ExploreAllLink>
                </DropdownMenu>
              </DropdownContainer>
            </NavLink>

            {/* Treks Dropdown */}
            <NavLink $white={useWhiteText} style={{ position: 'relative' }}>
              <DropdownContainer>
                <DropdownTrigger 
                  $white={useWhiteText}
                  onClick={() => setOpenDropdown(openDropdown === 'treks' ? null : 'treks')}
                >
                  Treks
                  <DropdownChevron $isOpen={openDropdown === 'treks'} />
                </DropdownTrigger>
                
                <DropdownMenu $isOpen={openDropdown === 'treks'} style={{ minWidth: '320px' }}>
                  {STATE_KEYS.map((stateKey) => {
                    const items = trekkingItineraries[stateKey];
                    if (!items || items.length === 0) return null;
                    return (
                      <React.Fragment key={`trek-${stateKey}`}>
                        <DropdownStateHeader>{STATE_LABELS[stateKey]}</DropdownStateHeader>
                        {items.slice(0, 3).map((trek: any, idx: number) => (
                          <BikePreviewItem key={`trek-${stateKey}-${idx}`} onClick={() => handleTrekSelect(trek)}>
                            <BikeItemTitle>{trek.trekName || trek.name}</BikeItemTitle>
                            <BikeItemMeta>
                              <span>{trek.duration ? `${trek.duration} Days` : ''}</span>
                              <span>{trek.difficulty || ''}</span>
                            </BikeItemMeta>
                          </BikePreviewItem>
                        ))}
                        <DropdownDivider />
                      </React.Fragment>
                    );
                  })}
                  <ExploreAllLink onClick={() => {
                    setOpenDropdown(null);
                    if (onNavigateTo) onNavigateTo('curated-experiences', { destination: null, fromHomepage: true, tourType: 'trek' });
                  }}>
                    Explore All Treks →
                  </ExploreAllLink>
                </DropdownMenu>
              </DropdownContainer>
            </NavLink>

            {/* Bikes Dropdown */}
            <NavLink $white={useWhiteText} style={{ position: 'relative' }}>
              <DropdownContainer>
                <DropdownTrigger 
                  $white={useWhiteText}
                  onClick={() => setOpenDropdown(openDropdown === 'bikes' ? null : 'bikes')}
                >
                  Bikes
                  <DropdownChevron $isOpen={openDropdown === 'bikes'} />
                </DropdownTrigger>
                
                <DropdownMenu $isOpen={openDropdown === 'bikes'} style={{ minWidth: '320px' }}>
                  {STATE_KEYS.map((stateKey) => {
                    const items = bikeItineraries[stateKey];
                    if (!items || items.length === 0) return null;
                    return (
                      <React.Fragment key={`bike-${stateKey}`}>
                        <DropdownStateHeader>{STATE_LABELS[stateKey]}</DropdownStateHeader>
                        {items.slice(0, 3).map((bike: any, idx: number) => (
                          <BikePreviewItem key={`bike-${stateKey}-${idx}`} onClick={() => handleBikeSelect(bike)}>
                            <BikeItemTitle>{bike.title || bike.name}</BikeItemTitle>
                            <BikeItemMeta>
                              <span>{bike.packageDuration}</span>
                              <span>{bike.totalDistance}</span>
                            </BikeItemMeta>
                          </BikePreviewItem>
                        ))}
                        <DropdownDivider />
                      </React.Fragment>
                    );
                  })}
                  <ExploreAllLink onClick={() => {
                    setOpenDropdown(null);
                    if (onNavigateTo) onNavigateTo('curated-experiences', { destination: null, fromHomepage: true, tourType: 'bike' });
                  }}>
                    Explore All Bikes →
                  </ExploreAllLink>
                </DropdownMenu>
              </DropdownContainer>
            </NavLink>

            <NavLink active={currentPage === 'about'} $white={useWhiteText}>
              <a onClick={() => handleNavClick('about')}>About</a>
            </NavLink>

            <NavLink active={currentPage === 'contact'} $white={useWhiteText}>
              <a onClick={() => handleNavClick('contact')}>Contact</a>
            </NavLink>
          </NavLinks>
        </Nav>
      </Container>
    </HeaderSection>

    {/* Mobile Menu — rendered outside HeaderSection to escape backdrop-filter containing block */}
    <MobileMenuOverlay $isOpen={mobileMenuOpen} onClick={closeMobileMenu} />
    <MobileMenuDrawer $isOpen={mobileMenuOpen}>
        <MobileDrawerHeader>
          <MobileDrawerTitle>HIMALAYAN ROVERS</MobileDrawerTitle>
          <MobileCloseButton onClick={closeMobileMenu} aria-label="Close menu">&times;</MobileCloseButton>
        </MobileDrawerHeader>
        <MobileNavList>
          <MobileNavItem $active={currentPage === 'home'} onClick={() => handleNavClick('home')}>Home</MobileNavItem>

          {/* Destinations */}
          <MobileDropdownToggle onClick={() => setMobileDropdown(mobileDropdown === 'destinations' ? null : 'destinations')}>
            Destinations
            <MobileDropdownChevron $isOpen={mobileDropdown === 'destinations'} />
          </MobileDropdownToggle>
          <MobileDropdownItems $isOpen={mobileDropdown === 'destinations'}>
            {STATE_KEYS.map((stateKey) => {
              const items = destinationItineraries[stateKey];
              if (!items || items.length === 0) return null;
              return (
                <React.Fragment key={`m-dest-${stateKey}`}>
                  <MobileDropdownLabel>{STATE_LABELS[stateKey]}</MobileDropdownLabel>
                  {items.slice(0, 3).map((itinerary: any, idx: number) => (
                    <MobileDropdownItem key={`m-${stateKey}-${idx}`} onClick={() => handleDestinationSelect(itinerary)}>
                      {itinerary.title || itinerary.name}
                    </MobileDropdownItem>
                  ))}
                </React.Fragment>
              );
            })}
            <MobileDropdownItem onClick={() => { closeMobileMenu(); if (onNavigateTo) onNavigateTo('curated-experiences', { destination: null, fromHomepage: true, tourType: 'car' }); }}>
              Explore All Destinations →
            </MobileDropdownItem>
          </MobileDropdownItems>

          {/* Treks */}
          <MobileDropdownToggle onClick={() => setMobileDropdown(mobileDropdown === 'treks' ? null : 'treks')}>
            Treks
            <MobileDropdownChevron $isOpen={mobileDropdown === 'treks'} />
          </MobileDropdownToggle>
          <MobileDropdownItems $isOpen={mobileDropdown === 'treks'}>
            {STATE_KEYS.map((stateKey) => {
              const items = trekkingItineraries[stateKey];
              if (!items || items.length === 0) return null;
              return (
                <React.Fragment key={`m-trek-${stateKey}`}>
                  <MobileDropdownLabel>{STATE_LABELS[stateKey]}</MobileDropdownLabel>
                  {items.slice(0, 3).map((trek: any, idx: number) => (
                    <MobileDropdownItem key={`m-trek-${stateKey}-${idx}`} onClick={() => handleTrekSelect(trek)}>
                      {trek.trekName || trek.name}
                    </MobileDropdownItem>
                  ))}
                </React.Fragment>
              );
            })}
            <MobileDropdownItem onClick={() => { closeMobileMenu(); if (onNavigateTo) onNavigateTo('curated-experiences', { destination: null, fromHomepage: true, tourType: 'trek' }); }}>
              Explore All Treks →
            </MobileDropdownItem>
          </MobileDropdownItems>

          {/* Bikes */}
          <MobileDropdownToggle onClick={() => setMobileDropdown(mobileDropdown === 'bikes' ? null : 'bikes')}>
            Bikes
            <MobileDropdownChevron $isOpen={mobileDropdown === 'bikes'} />
          </MobileDropdownToggle>
          <MobileDropdownItems $isOpen={mobileDropdown === 'bikes'}>
            {STATE_KEYS.map((stateKey) => {
              const items = bikeItineraries[stateKey];
              if (!items || items.length === 0) return null;
              return (
                <React.Fragment key={`m-bike-${stateKey}`}>
                  <MobileDropdownLabel>{STATE_LABELS[stateKey]}</MobileDropdownLabel>
                  {items.slice(0, 3).map((bike: any, idx: number) => (
                    <MobileDropdownItem key={`m-bike-${stateKey}-${idx}`} onClick={() => handleBikeSelect(bike)}>
                      {bike.title || bike.name}
                    </MobileDropdownItem>
                  ))}
                </React.Fragment>
              );
            })}
            <MobileDropdownItem onClick={() => { closeMobileMenu(); if (onNavigateTo) onNavigateTo('curated-experiences', { destination: null, fromHomepage: true, tourType: 'bike' }); }}>
              Explore All Bikes →
            </MobileDropdownItem>
          </MobileDropdownItems>

          <MobileNavItem $active={currentPage === 'about'} onClick={() => handleNavClick('about')}>About</MobileNavItem>
          <MobileNavItem $active={currentPage === 'contact'} onClick={() => handleNavClick('contact')}>Contact</MobileNavItem>
        </MobileNavList>
    </MobileMenuDrawer>
    </>
  );
};

export default AppHeader;
